// packages/shared-i18n/i18n.ts
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import zhCN from "./locales/zh-CN.json";

export const LANGUAGE_STORAGE_KEY = "app-language";

// 定义所有支持的语言类型
export type LanguageType =
  | "zh-CN"
  | "en-US"
  | "ko-KR"
  | "ja-JP"
  | "de-DE"
  | "fr-FR"
  | "es-ES"
  | "ru-RU"
  | "tr-TR";

// 语言名称映射（前端展示用）
export const LANGUAGES = {
  "zh-CN": "中文",
  "en-US": "English",
  "ko-KR": "한국어",
  "ja-JP": "日本語",
  "de-DE": "Deutsch",
  "fr-FR": "Français",
  "es-ES": "Español",
  "ru-RU": "Русский",
  "tr-TR": "Türkçe",
};

// 获取本地存储的语言
export const getStoredLanguage = (): LanguageType => {
  try {
    if (typeof window !== "undefined" && window.localStorage) {
      const v = window.localStorage.getItem(
        LANGUAGE_STORAGE_KEY
      ) as LanguageType | null;
      return v || "zh-CN";
    }
  } catch {
    // Ignore localStorage errors
  }
  return "zh-CN";
};

// 设置本地存储的语言
export const setStoredLanguage = (language: LanguageType): void => {
  try {
    if (typeof window !== "undefined" && window.localStorage) {
      window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
    }
  } catch {
    // Ignore localStorage errors
  }
};

// ✅ 确保只初始化一次
if (!i18n.isInitialized) {
  i18n.use(initReactI18next).init({
    resources: {
      "zh-CN": { translation: zhCN },
    },
    lng: getStoredLanguage(),
    fallbackLng: "zh-CN",
    interpolation: {
      escapeValue: false, // React 已自带转义，无需重复处理
    },
    react: {
      useSuspense: false,
      bindI18n: "languageChanged", // 监听语言变化自动更新组件
      bindI18nStore: "",
      transEmptyNodeValue: "",
      transSupportBasicHtmlNodes: true,
      transKeepBasicHtmlNodesFor: ["br", "strong", "i"],
    },
  });
}

// 记录已加载的语言，避免重复加载
const loaded: Record<LanguageType, boolean> = {
  "zh-CN": true,
  "en-US": false,
  "ko-KR": false,
  "ja-JP": false,
  "de-DE": false,
  "fr-FR": false,
  "es-ES": false,
  "ru-RU": false,
  "tr-TR": false,
};
const loading: Partial<Record<LanguageType, Promise<void>>> = {};

/**
 * 确保指定语言的资源已加载（懒加载核心函数）
 * @param lng 要加载的语言标识
 */
async function ensureLanguageLoaded(lng: LanguageType) {
  if (loaded[lng]) return;
  if (loading[lng]) {
    return loading[lng];
  }

  loading[lng] = (async () => {
    try {
      let mod: any = null;
      switch (lng) {
        case "en-US":
          mod = await import("./locales/en-US.json");
          break;
        case "ko-KR":
          mod = await import("./locales/ko-KR.json");
          break;
        case "ja-JP":
          mod = await import("./locales/ja-JP.json");
          break;
        case "de-DE":
          mod = await import("./locales/de-DE.json");
          break;
        case "fr-FR":
          mod = await import("./locales/fr-FR.json");
          break;
        case "es-ES":
          mod = await import("./locales/es-ES.json");
          break;
        case "ru-RU":
          mod = await import("./locales/ru-RU.json");
          break;
        case "tr-TR":
          mod = await import("./locales/tr-TR.json");
          break;
        case "zh-CN":
          return;
      }

      if (mod) {
        const data = mod.default;
        // 始终 deep merge：非 eweadn 品牌在 applyBrandLocale 可能已写入仅含 brandName 的占位包，
        // 若因 hasResourceBundle 跳过合并会导致界面仍 fallback 到 zh-CN。
        i18n.addResourceBundle(lng, "translation", data, true, true);
        loaded[lng] = true;
      }
    } catch (error) {
      console.error(`[i18n] 加载语言资源失败: ${lng}`, error);
      loaded[lng] = false;
    } finally {
      delete loading[lng];
    }
  })();
  return loading[lng];
}

// ✅ 重写 changeLanguage 方法，确保加载资源后再切换
const originalChangeLanguage = i18n.changeLanguage.bind(i18n);
i18n.changeLanguage = async (lng: LanguageType) => {
  // 先加载资源，再执行原切换逻辑
  await ensureLanguageLoaded(lng);
  // 切换语言时同步存储到 localStorage
  setStoredLanguage(lng);
  return originalChangeLanguage(lng);
};

i18n.on("languageChanged", (lng) => {
  try {
    if (typeof document !== "undefined") {
      document.documentElement.setAttribute("lang", lng);
    }
    if (typeof window !== "undefined") {
      const w: any = window as any;
      const shouldBroadcast = w.__I18N_BROADCAST__ !== false;
      const last = w.__I18N_LAST_BROADCAST__;
      if (shouldBroadcast && last !== lng) {
        w.__I18N_LAST_BROADCAST__ = lng;
        if (w.__I18N_BC__) {
          try {
            w.__I18N_BC__.postMessage({ lang: lng });
          } catch {
            // Ignore postMessage errors
          }
        } else {
          window.dispatchEvent(
            new CustomEvent("appLanguageChange", { detail: { lang: lng } })
          );
        }
      }
    }
  } catch {
    // Ignore broadcast errors
  }
});

// ✅ 初始化时懒加载非中文默认语言（加载完成后切换一次以刷新文案）
const initialLanguage = getStoredLanguage();
if (!loaded[initialLanguage]) {
  void ensureLanguageLoaded(initialLanguage)
    .then(() => i18n.changeLanguage(initialLanguage))
    .catch((err) => {
      console.error("[i18n] 初始化懒加载语言失败:", err);
      void i18n.changeLanguage("zh-CN");
    });
}

const changeLanguageSilently = (next: LanguageType) => {
  const w: any = typeof window !== "undefined" ? (window as any) : undefined;
  const prev = w ? w.__I18N_BROADCAST__ : undefined;
  if (w) w.__I18N_BROADCAST__ = false;
  return i18n.changeLanguage(next).finally(() => {
    if (w) w.__I18N_BROADCAST__ = prev;
  });
};

if (typeof window !== "undefined") {
  const w: any = window as any;
  if (
    !w.__I18N_BC__ &&
    typeof (window as any).BroadcastChannel !== "undefined"
  ) {
    try {
      w.__I18N_BC__ = new (window as any).BroadcastChannel("app-language");
      w.__I18N_BC__.onmessage = (ev: any) => {
        const next = ev?.data?.lang as LanguageType;
        if (next && next !== i18n.language) {
          changeLanguageSilently(next);
        }
      };
    } catch {
      // Ignore BroadcastChannel errors
    }
  }
  if (!w.__I18N_LANG_EVT__) {
    const handler = ((event: any) => {
      const next = event?.detail?.lang as LanguageType;
      if (next && next !== i18n.language) {
        changeLanguageSilently(next);
      }
    }) as EventListener;
    w.__I18N_LANG_EVT__ = handler;
    window.addEventListener("appLanguageChange", handler);
  }
}

/** 统一切换语言：懒加载资源 → localStorage → i18n（主应用请优先用 globalStore.setLanguage 以同步 qiankun） */
export async function switchAppLanguage(lng: LanguageType) {
  return i18n.changeLanguage(lng);
}

/** 子应用从 qiankun globalState / localStorage 同步语言（与主应用 i18n 实例隔离时使用） */
export async function syncLanguageFromHost(language?: string) {
  const next = (language || getStoredLanguage()) as LanguageType;
  await ensureLanguageLoaded(next);

  if (i18n.language !== next) {
    return i18n.changeLanguage(next);
  }

  // init 时 lng 已与 localStorage 一致，但懒加载资源可能尚未注入，需强制刷新 UI
  await i18n.reloadResources(next);
  i18n.emit("languageChanged", next);
}

export default i18n;
